import { ActionConfirmationHost } from "../ui/ActionConfirmation.js";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useSearchParams } from "react-router-dom";
import { formatDate, formatDuration, formatTime } from "../format.js";
import { Pagination } from "../Pagination.js";
import { useAsync } from "../useAsync.js";
import {
  booking,
  type AppointmentEnrichment,
  type LessonCreditSummary,
  type StudentLesson,
  type StudentLessonDirection,
  type StudentLessonEnrichment,
  type StudentLessonPage,
} from "./api.js";
import { ChangeLesson, type ChangeMode } from "./ChangeLesson.js";
import { LessonChangeActions } from "./StudentDashboard.js";
import { describePayment, outcomeLabel } from "./policy.js";
import { studentBookingStatusLabel, studentLessonPaymentDescription } from "./studentLessonPresentation.js";
import { CARD_RECORD_PAGE_SIZE } from "../paginationPageSizes.js";
import "./familyLessons.css";

export const LESSONS_PAGE_SIZE = CARD_RECORD_PAGE_SIZE;
const LESSON_DIRECTIONS = ["upcoming", "past"] as const;

export function lessonTabForKey(
  current: StudentLessonDirection,
  key: string,
): StudentLessonDirection | null {
  if (key === "Home") return LESSON_DIRECTIONS[0];
  if (key === "End") return LESSON_DIRECTIONS.at(-1) ?? null;
  if (key !== "ArrowLeft" && key !== "ArrowRight") return null;
  return current === "upcoming" ? "past" : "upcoming";
}

export interface LessonPageView {
  student: StudentLessonPage["student"];
  lessons: StudentLesson[];
  enrichment: StudentLessonEnrichment["state"] | "pending";
}

/** Merge only the visible page with the optional policy batch response. */
export function mergeLessonPage(
  page: StudentLessonPage,
  enrichment: StudentLessonEnrichment | null,
  enrichmentError: string | null,
): LessonPageView {
  const enriched = new Map<number, AppointmentEnrichment>(
    (enrichment?.appointments ?? []).map((row) => [row.booking_id, row]),
  );
  const unavailable = new Set(enrichment?.unavailableBookingIds ?? []);
  const lessons = page.lessons.map((lesson) => {
    const bookingId = Number(lesson.bookingId);
    return {
      ...lesson,
      enrichment: lesson.domain === "private" ? enriched.get(bookingId) ?? null : null,
      unavailable: lesson.domain === "private" && unavailable.has(bookingId),
    } satisfies StudentLesson;
  });

  return {
    student: page.student,
    lessons,
    enrichment: enrichmentError ? "unavailable" : enrichment ? enrichment.state : "pending",
  };
}

function numericBookingIds(
  lessons: readonly {
    bookingId: string;
    domain?: "private" | "school";
    canManageBooking?: boolean;
  }[],
): number[] {
  return [...new Set(
    lessons
      .filter((lesson) => lesson.domain === "private" && lesson.canManageBooking !== false)
      .map((lesson) => Number(lesson.bookingId)),
  )]
    .filter((id) => Number.isInteger(id) && id > 0)
    .slice(0, 100);
}

interface LessonEnrichmentState {
  key: string;
  data: StudentLessonEnrichment | null;
  error: string | null;
  loading: boolean;
}

/** Unlike the general async helper, policy data must never survive a page key change. */
function useLessonEnrichment(
  studentId: string,
  direction: StudentLessonDirection,
  bookingIds: readonly number[],
) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const bookingKey = bookingIds.join(",");
  const key = `${studentId}:${direction}:${bookingKey}:${reloadNonce}`;
  const [state, setState] = useState<LessonEnrichmentState>(() => ({
    key,
    data: null,
    error: null,
    loading: true,
  }));

  useEffect(() => {
    let current = true;
    if (bookingIds.length === 0) {
      setState({ key, data: null, error: null, loading: false });
      return () => {
        current = false;
      };
    }

    setState({ key, data: null, error: null, loading: true });
    booking
      .lessonEnrichment(studentId, [...bookingIds])
      .then((data) => {
        if (current) setState({ key, data, error: null, loading: false });
      })
      .catch((cause: unknown) => {
        if (!current) return;
        setState({
          key,
          data: null,
          error: cause instanceof Error ? cause.message : "Something went wrong.",
          loading: false,
        });
      });

    return () => {
      current = false;
    };
  }, [studentId, direction, bookingKey, key]);

  return {
    ...(state.key === key
      ? state
      : { key, data: null, error: null, loading: true }),
    reload: () => setReloadNonce((value) => value + 1),
  };
}

function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / LESSONS_PAGE_SIZE));
}

function rangeLabel(total: number, offset: number, count: number): string {
  if (total === 0 || count === 0) return "0 of 0";
  return `${offset + 1}–${Math.min(offset + count, total)} of ${total}`;
}

export function Lessons({ studentId }: { studentId: string }) {
  return <ActionConfirmationHost key={studentId}><LessonsContent studentId={studentId} /></ActionConfirmationHost>;
}

function LessonsContent({ studentId }: { studentId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get("view");
  const direction: StudentLessonDirection = requestedView === "past" ? "past" : "upcoming";
  const [focusedDirection, setFocusedDirection] = useState<StudentLessonDirection>(direction);
  const tabsId = useId();
  const [pages, setPages] = useState<Record<StudentLessonDirection, number>>({
    upcoming: 1,
    past: 1,
  });
  const pageNumber = pages[direction];
  const offset = (pageNumber - 1) * LESSONS_PAGE_SIZE;
  const page = useAsync(
    () => booking.lessonList(studentId, direction, LESSONS_PAGE_SIZE, offset),
    [studentId, direction, pageNumber],
  );
  const credits = useAsync(
    () => direction === "upcoming" ? booking.credits(studentId) : Promise.resolve(null),
    [studentId, direction],
  );
  const pageIsCurrent =
    page.data !== null &&
    page.data.student.id === studentId &&
    page.data.direction === direction &&
    page.data.offset === offset;
  const visibleRows = pageIsCurrent ? page.data?.lessons ?? [] : [];
  const bookingIds = numericBookingIds(visibleRows);
  const enrichment = useLessonEnrichment(studentId, direction, bookingIds);
  const [changing, setChanging] = useState<{
    bookingId: string;
    mode: ChangeMode;
  } | null>(null);

  useEffect(() => {
    if (requestedView === null || requestedView === "past") return;
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, requestedView]);

  const view = pageIsCurrent && page.data
    ? mergeLessonPage(
        page.data,
        enrichment.data,
        enrichment.error,
      )
    : null;
  const total = view && page.data ? page.data.total : 0;
  const pagesTotal = pageCount(total);

  useEffect(() => {
    if (pageIsCurrent && pageNumber > pagesTotal) {
      setPages((current) => ({ ...current, [direction]: pagesTotal }));
    }
  }, [direction, pageIsCurrent, pageNumber, pagesTotal]);

  const tabId = (value: StudentLessonDirection) => `${tabsId}-${value}-tab`;
  const panelId = (value: StudentLessonDirection) => `${tabsId}-${value}-panel`;

  function changeDirection(next: StudentLessonDirection) {
    setFocusedDirection(next);
    setChanging(null);
    const params = new URLSearchParams(searchParams);
    if (next === "past") params.set("view", "past");
    else params.delete("view");
    setSearchParams(params);
  }

  if (!pageIsCurrent) {
    if (page.error) {
      return <div className="error" role="alert">
        We could not load your lessons right now. <button type="button" className="quiet small" onClick={page.reload}>Retry lessons</button>
      </div>;
    }
    return (
      <section className="family-lessons" aria-labelledby="lessons-heading">
        <h1 id="lessons-heading">Lessons</h1>
        <LessonTabs
          tabsId={tabsId}
          direction={direction}
          focusedDirection={focusedDirection}
          onFocusDirection={setFocusedDirection}
          onChange={changeDirection}
        />
        {LESSON_DIRECTIONS.map((value) => (
          <div
            id={panelId(value)}
            key={value}
            role="tabpanel"
            aria-labelledby={tabId(value)}
            hidden={direction !== value}
          >
            {direction === value && (
              <p className="empty" role="status">
                Loading your lessons…
              </p>
            )}
          </div>
        ))}
      </section>
    );
  }

  if (!page.data || !view) return null;
  const currentPage = page.data;

  const setPage = (nextPage: number) => {
    setPages((current) => ({ ...current, [direction]: nextPage }));
    setChanging(null);
  };
  const refresh = () => {
    page.reload();
    enrichment.reload();
    credits.reload();
  };
  const changesUnavailable =
    direction === "upcoming" &&
    view.lessons.some((lesson) => lesson.domain === "private") &&
    view.enrichment === "unavailable";

  return (
    <section className="family-lessons" aria-labelledby="lessons-heading">
      <div className="family-lessons-heading">
        <div>
          <h1 id="lessons-heading">Lessons</h1>
          <p className="muted">Your scheduled lessons and lesson history.</p>
        </div>
      </div>

      <LessonTabs
        tabsId={tabsId}
        direction={direction}
        focusedDirection={focusedDirection}
        onFocusDirection={setFocusedDirection}
        onChange={changeDirection}
      />

      {LESSON_DIRECTIONS.map((value) => (
        <div
          id={panelId(value)}
          key={value}
          role="tabpanel"
          aria-labelledby={tabId(value)}
          hidden={direction !== value}
        >
          {direction === value && (
            <>
              {changesUnavailable && (
                <div className="notice" role="status">
                  Lesson changes are temporarily unavailable. Your lesson times are still shown.
                </div>
              )}

              {view.lessons.length === 0 ? (
                <div className="panel family-lessons-empty">
                  <strong>{value === "upcoming" ? "No upcoming lessons" : "No past lessons"}</strong>
                  <p className="muted">
                    {value === "upcoming"
                      ? "There is nothing booked at the moment."
                      : "Your lesson history will appear here."}
                  </p>
                </div>
              ) : (
                <div className="family-lessons-list" aria-live="polite">
                  {view.lessons.map((lesson) => {
                    const open = changing?.bookingId === lesson.bookingId;
                    const openMode = open ? changing.mode : null;
                    return (
                      <LessonRow
                        key={`${value}:${lesson.bookingId}`}
                        direction={value}
                        lesson={lesson}
                        credits={credits.data?.portal_student_id === studentId ? credits.data : null}
                        open={open}
                        openMode={openMode}
                        onManage={
                          value === "upcoming"
                            ? (mode) =>
                                setChanging((current) =>
                                  current?.bookingId === lesson.bookingId && current.mode === mode
                                    ? null
                                    : { bookingId: lesson.bookingId, mode },
                                )
                            : undefined
                        }
                        onDone={refresh}
                        onClose={() => setChanging(null)}
                        policyState={view.enrichment}
                        studentId={studentId}
                      />
                    );
                  })}
                </div>
              )}

              <div className="family-lessons-pagination-summary muted small">
                {rangeLabel(currentPage.total, currentPage.offset, view.lessons.length)}
              </div>
              <Pagination
                ariaLabel={`${value === "upcoming" ? "Upcoming" : "Past"} lessons pagination`}
                page={Math.min(pageNumber, pagesTotal)}
                pages={pagesTotal}
                disabled={page.loading}
                onPageChange={setPage}
              />
              {page.loading && (
                <p className="small muted" role="status">
                  Refreshing lessons…
                </p>
              )}
            </>
          )}
        </div>
      ))}
    </section>
  );
}

function LessonTabs({
  tabsId,
  direction,
  focusedDirection,
  onFocusDirection,
  onChange,
}: {
  tabsId: string;
  direction: StudentLessonDirection;
  focusedDirection: StudentLessonDirection;
  onFocusDirection: (direction: StudentLessonDirection) => void;
  onChange: (direction: StudentLessonDirection) => void;
}) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function onKeyDown(
    event: ReactKeyboardEvent<HTMLButtonElement>,
    current: StudentLessonDirection,
  ) {
    const next = lessonTabForKey(current, event.key);
    if (next === null) return;
    event.preventDefault();
    const index = LESSON_DIRECTIONS.indexOf(next);
    onFocusDirection(next);
    tabRefs.current[index]?.focus();
  }

  return (
    <div
      className="family-lessons-tabs"
      role="tablist"
      aria-label="Lesson views"
      aria-orientation="horizontal"
    >
      {LESSON_DIRECTIONS.map((value, index) => (
        <button
          aria-selected={direction === value}
          className={direction === value ? "is-active" : "quiet"}
          aria-controls={`${tabsId}-${value}-panel`}
          id={`${tabsId}-${value}-tab`}
          key={value}
          onFocus={() => onFocusDirection(value)}
          onKeyDown={(event) => onKeyDown(event, value)}
          onClick={() => onChange(value)}
          ref={(node) => {
            tabRefs.current[index] = node;
          }}
          tabIndex={focusedDirection === value ? 0 : -1}
          role="tab"
          type="button"
        >
          {value === "upcoming" ? "Upcoming" : "Past"}
        </button>
      ))}
    </div>
  );
}

function LessonRow({
  direction,
  lesson,
  credits,
  open,
  openMode,
  onManage,
  onDone,
  onClose,
  policyState,
  studentId,
}: {
  direction: StudentLessonDirection;
  lesson: StudentLesson;
  credits: LessonCreditSummary | null;
  open: boolean;
  openMode: ChangeMode | null;
  onManage: ((mode: ChangeMode) => void) | undefined;
  onDone: () => void;
  onClose: () => void;
  policyState: LessonPageView["enrichment"];
  studentId: string;
}) {
  const duration = lessonDuration(lesson);
  const status = lesson.enrichment?.booking_status ?? lesson.status;
  const payment = direction === "upcoming"
    ? studentLessonPaymentDescription(lesson, credits, studentId)
    : lesson.enrichment ? describePayment(lesson.enrichment.payment) : null;
  const school = lesson.domain === "school";
  const location = lessonLocation(lesson);

  return (
    <article className="panel family-lesson-row">
      <div className="family-lesson-row-main">
        <div className="family-lesson-date">
          <time dateTime={lesson.startsAt}>{formatDate(lesson.startsAt)}</time>
          <strong>
            {formatTime(lesson.startsAt)}{duration ? `–${formatTime(lesson.endsAt)}` : ""}
          </strong>
        </div>
        <div className="family-lesson-details">
          <strong>{lesson.serviceName ?? (school ? "School lesson" : "Lesson")}</strong>
          {lesson.tutorName && <div>{lesson.tutorName}</div>}
          {duration && <div className="small muted">{duration}</div>}
          {location && <div className="small muted">{location}</div>}
          <div className="family-lesson-statuses">
            <span className="tag">{school ? "School lesson" : studentBookingStatusLabel(status)}</span>
            {payment && <span className="tag">{payment}</span>}
            {lesson.enrichment?.credit.lesson_block_id && (
              <span className="tag">Lesson credit</span>
            )}
            {direction === "past" && lesson.enrichment && (
              <span className="tag">
                {outcomeLabel(lesson.enrichment.attendance_outcome, lesson.enrichment.booking_status)}
              </span>
            )}
          </div>
        </div>
      </div>

      {direction === "upcoming" && (
        <div className="family-lesson-actions" aria-label="Lesson actions">
          <LessonChangeActions
            lesson={lesson}
            onManage={onManage}
            open={open}
            openMode={openMode}
            policyState={policyState}
            variant="compact"
          />
        </div>
      )}

      {open && openMode && (
        <div className="family-lesson-change">
          <ChangeLesson
            initialMode={openMode}
            key={`${lesson.bookingId}:${openMode}`}
            lesson={lesson}
            onClose={onClose}
            onDone={onDone}
            studentId={studentId}
          />
        </div>
      )}
    </article>
  );
}

function lessonDuration(lesson: StudentLesson): string | null {
  const seconds = (Date.parse(lesson.endsAt) - Date.parse(lesson.startsAt)) / 1000;
  return Number.isFinite(seconds) && seconds > 0 ? formatDuration(seconds) : null;
}

function lessonLocation(lesson: StudentLesson): string | null {
  const candidate = lesson as StudentLesson & { locationName?: unknown; location?: unknown };
  if (typeof candidate.locationName === "string" && candidate.locationName.trim()) {
    return candidate.locationName.trim();
  }
  if (typeof candidate.location === "string" && candidate.location.trim()) {
    return candidate.location.trim();
  }
  return null;
}

export {
  StudentDashboard,
  StudentDashboardView,
  studentDashboardIdentityMatches,
  studentDashboardLoadState,
} from "./StudentDashboard.js";
export { StudentDashboard as StudentBooking } from "./StudentDashboard.js";
